import React, { memo, ReactNode } from 'react'
import ReactMarkdown, { Components, defaultUrlTransform } from 'react-markdown'
import remarkGfm from 'remark-gfm'
import { CitationToken } from 'components/messages/citations/CitationToken'
import { CitationSource } from 'types/chat-message'
import { MarkdownLink } from 'components/ui/MarkdownLink'

type MarkdownTextProps = {
  text: string
  className?: string
  sources?: Record<string, CitationSource> | null
}

// [N] remote source, [img:N] image, [chart:N] inline chart, [att:N] user-uploaded attachment.
const CITATION_SPLIT_REGEX = /\[(img:|chart:|att:)?(\d+)\]/g

// react-markdown strips tel: by default (not in its safe-protocol list), which would
// blank the href and open the widget itself. Keep tel: as-is, sanitize the rest.
const urlTransform = (url: string) => (/^tel:/i.test(url) ? url : defaultUrlTransform(url))

function processCitationChildren(children: ReactNode, sources?: Record<string, CitationSource> | null): ReactNode {
  return React.Children.map(children, (child) => {
    if (typeof child === 'string') {
      const parts = child.split(CITATION_SPLIT_REGEX)
      if (parts.length <= 1) return child
      const result: ReactNode[] = []
      for (let i = 0; i < parts.length; i += 3) {
        const text = parts[i]
        if (text) result.push(text)

        const flag = parts[i + 1]
        const num = parts[i + 2]
        if (num === undefined) continue

        result.push(<CitationToken key={`cite-${i}`} flag={flag} num={num} sources={sources} />)
      }
      return result
    }
    if (React.isValidElement<{ children?: ReactNode }>(child) && child.props?.children) {
      return React.cloneElement(child, {}, processCitationChildren(child.props.children, sources))
    }
    return child
  })
}

const MarkdownTextComponent = ({ text, className, sources }: MarkdownTextProps) => {
  const pc = (children: unknown) => processCitationChildren(children as ReactNode, sources)

  const components: Components = {
    h1: ({ children, ...props }) => (
      <h1 className="mt-6 mb-4 text-3xl font-bold" {...props}>
        {pc(children)}
      </h1>
    ),
    h2: ({ children, ...props }) => (
      <h2 className="mt-5 mb-3 text-2xl font-bold" {...props}>
        {pc(children)}
      </h2>
    ),
    h3: ({ children, ...props }) => (
      <h3 className="mt-4 mb-2 text-xl font-bold" {...props}>
        {pc(children)}
      </h3>
    ),

    ul: ({ children, ...props }) => (
      <ul className="list-disc pl-4" {...props}>
        {children}
      </ul>
    ),
    ol: ({ children, ...props }) => (
      <ol className="list-decimal pl-4" {...props}>
        {children}
      </ol>
    ),
    li: ({ children, ...props }) => (
      <li {...props} style={{ paddingInlineStart: 0 }}>
        {pc(children)}
      </li>
    ),

    p: ({ children, ...props }) => <p {...props}>{pc(children)}</p>,
    strong: ({ children, ...props }) => (
      <strong className="font-bold" {...props}>
        {children}
      </strong>
    ),
    em: ({ children, ...props }) => (
      <em className="italic" {...props}>
        {children}
      </em>
    ),

    // react-markdown 9 has no `inline` flag: a fenced block carries a language class or newlines,
    // a `field` span carries neither and must stay inline instead of breaking the sentence.
    code: ({ children, className, ...props }) => {
      const isBlock = /language-/.test(className ?? '') || String(children).includes('\n')
      return (
        <code
          className={
            isBlock
              ? 'block overflow-x-auto rounded-sm bg-muted p-3 font-mono text-sm'
              : 'rounded-sm bg-muted px-1 py-0.5 font-mono text-[0.9em]'
          }
          {...props}
        >
          {children}
        </code>
      )
    },

    a: MarkdownLink,

    blockquote: ({ children, ...props }) => (
      <blockquote className="my-2 border-l-4 border-border pl-4 italic" {...props}>
        {pc(children)}
      </blockquote>
    ),

    table: ({ children, ...props }) => (
      <div className="my-4 overflow-x-auto">
        <table className="min-w-full border-collapse border border-border" {...props}>
          {children}
        </table>
      </div>
    ),
    thead: ({ children, ...props }) => (
      <thead className="bg-muted/50" {...props}>
        {children}
      </thead>
    ),
    tr: ({ children, ...props }) => (
      <tr className="border-b border-border" {...props}>
        {children}
      </tr>
    ),
    th: ({ children, ...props }) => (
      <th className="border-r border-border px-4 py-2 text-left" {...props}>
        {pc(children)}
      </th>
    ),
    td: ({ children, ...props }) => (
      <td className="border-r border-border px-4 py-2" {...props}>
        {pc(children)}
      </td>
    ),
  }

  return (
    <div className={`max-w-none ${className || ''}`}>
      <ReactMarkdown remarkPlugins={[remarkGfm]} components={components} urlTransform={urlTransform}>
        {text}
      </ReactMarkdown>
    </div>
  )
}

export const MarkdownText = memo(MarkdownTextComponent)
